import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@mui/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import Chip from '@mui/material/Chip';
import { amber, blue, green, red } from '@mui/material/colors';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CellTooltip from './CellTooltip';
import { GAM_LINE_PRIMARY, Redirect } from '../../utils/link';
import { density, getDensityClass } from './utils/density';
import TextField from "@mui/material/TextField";

const styles = theme => ({
  td: {
    display: 'table-cell',
    textAlign: 'left',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    verticalAlign: 'inherit'
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 700
  },
  success: {
    background: green[600],
    color: theme.palette.common.white
  },
  info: {
    background: blue.A200,
    color: theme.palette.common.white
  },
  warn: {
    background: amber.A400,
    color: theme.palette.common.black
  },
  error: {
    background: red.A200,
    color: theme.palette.common.white
  },
  ...density
});

class DataCell extends Component {

  constructor(props) {
    super(props);
    this.state = {
      editMode: props.editable === 'always',
      datePickerOpen: false
    };
  }

  componentDidMount() {
    const { editable } = this.props;
    if (editable === 'always') {
      this.setState({ editMode: true });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.editable !== prevProps.editable) {
      this.setState({
        editMode: this.props.editable === 'always',
        datePickerOpen: false
      });
    }
  }

  formatNumber = value => {
    if (Number.isNaN(value)) {
      value = Number(value);
    }
    return value.toLocaleString('en-US');
  };

  formatDate = (value, format)=> {
    return moment(value).format(format);
  };

  formatCurrency = (value, currency) => {
    return Number(value).toLocaleString(undefined, {
      style: 'currency',
      currency
    });
  };

  makeBadge = (value, badge) => {
    const { classes, settings } = this.props;
    if (!value) {
      return value;
    }
    let badgeClass;
    if (['COMPLETED', 'NEEDS_UPDATE', 'UPDATED', 'NOT AD MANAGER'].includes(value)) {
      badgeClass = value;
    } else {
      badgeClass = 'ERROR';
    }
    return (
      <Chip
        label={value}
        classes={{
          root: getDensityClass(classes, 'Badge', settings.density)
        }}
        className={classNames(classes[badge[badgeClass]], 'full-width')}
      />
    )
  };

  makeBadge2 = (value) => { // ToDo: figure this out
    // const { classes } = this.props;
    if (!value) {
      value = 0;
    }
    return value;
    // return (<Chip label={value}/>)
  };

  // Sometimes there are multiple links.
  // If value is an array, the elements may or may not be formatted as name:id
  makeLink = (value, link) => {
    const { classes } = this.props;
    if (Array.isArray(value)) {
      return value.map(v => {
        const name_id = v.split(':');
        const id = name_id.pop();
        const name = name_id.length ? name_id[0] : id;
        const url = GAM_LINE_PRIMARY.replace('{dfpLineItemId}', id);
        return <>
          <Redirect to={url} className={classes.link} value={name} tooltip={id}/>
          <span>&nbsp; &nbsp;</span>
        </>;
      });
    }
    return <Redirect to={link} className={classes.link} value={value}/>;
  };

  notifyDataChange = newValue => {
    const { onEdit, dataRow, id } = this.props;
    if (onEdit) {
      onEdit(dataRow, id, newValue, true);
    }
  };

  handleDateChange = d => {
    const dNoon = moment(d).set({ h: 12, m: 0, s: 0 }).toISOString();
    this.notifyDataChange(dNoon);
    const { editable } = this.props;
    this.setState({ editValue: d });
    if (editable !== 'always') {
      this.setState({ editMode: false });
    }
  };

  handleNumberChange = (id, value) => {
    const { dataRow } = this.props;
    if (id === 'contractedQuantity' && dataRow.data.rateType && ['CPD', 'Flat Fee'].includes(dataRow.data.rateType)) {
      value = this.state.editValue;
      return;
    }
    if (value < 0) {
      value = 0;
    }
    this.setState({ editValue: value })
  };

  doneEditing = (value) => {
    this.setState({
      editMode: false
    });
    this.notifyDataChange(value);
  };

  getSelectOption = val => {
    return <MenuItem key={val} value={val}>{val}</MenuItem>
  };

  makeEditable = () => {
    const { classes, date, numeric, theme, select, settings, editable, currency, id, value } = this.props;
    const { editValue } = this.state;
    const { fontSize } = theme.typography;
    const textAlign = numeric ? 'right' : 'left';
    const tdClasses = classNames(
      classes.td,
      getDensityClass(classes, 'Cell', settings.density),
      getDensityClass(classes, 'Row', settings.density),
    );
    let editor;
    if (select && select.length > 0) {
       editor = (
         <Select
           variant="standard"
           value={value}
           onChange={(evt) => {
             this.notifyDataChange(evt.target.value);
           }}>
           {select.map(this.getSelectOption)}
         </Select>
       )
    } else if (date) {
      const val = moment(value);
      editor = (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            onOpen={this.openDatePicker}
            onClose={this.closeDatePicker}
            autoOk
            value={val}
            inputFormat="MM/DD/YYYY"
            component="span"
            onChange={this.handleDateChange}
            onKeyDown={this.doneEditing}
            animateYearScrolling={false}
            renderInput={(params) => <TextField {...params} />}
          />
        </LocalizationProvider>
      )
    } else if (numeric || currency) {
      editor = (
        <Input
          type={'number'}
          fullWidth
          value={editValue}
          autoFocus={editable !== 'always'}
          inputProps={{
            style: { textAlign: 'center', fontSize }
          }}
          onChange={evt => this.handleNumberChange(id, evt.target.value)}
          onBlur={evt => this.doneEditing(evt.target.value)}
          onKeyDown={(evt) => {
            if (evt.keyCode === 13) this.doneEditing(evt.target.value);
          }}
        />
      )
    } else {
      editor = (
        <Input
          type={'text'}
          fullWidth
          value={editValue}
          autoFocus={editable !== 'always'}
          inputProps={{
            style: { textAlign: 'center', fontSize }
          }}
          onChange={evt => this.setState({ editValue: evt.target.value })}
          onBlur={evt => this.doneEditing(evt.target.value)}
          onKeyDown={(evt) => {
            if (evt.keyCode === 13) this.doneEditing(evt.target.value);
          }}
        />
      )
    }

    return (
      <ClickAwayListener onClickAway={this.closeEditor}>
        <FormControl
          variant="standard"
          component='td'
          className={tdClasses}
          style={{textAlign}}>
          {editor}
        </FormControl>
      </ClickAwayListener>
    );
  }

  handleClick = evt => {
    if (this.tooltipRef && this.tooltipRef.current) {
      this.tooltipRef.current.hide();
    }
  }

  openDatePicker = () => {
    this.setState({
      datePickerOpen: true
    })
  }

  closeDatePicker = () => {
    this.setState({
      datePickerOpen: false
    })
  }

  openEditor = evt => {
    const { editable, value } = this.props;
    if (editable === 'always') {
      return;
    }

    if (editable) {
      this.setState({
        editMode: true,
        editValue: value
      });
    }
  }

  closeEditor = () => {
    // closeEditor is called when the ClickAwayListener.onClickAway event fires.
    // It would prevent datepicker events from being processed, so if datepicker is open we will ignore it.
    const { editable } = this.props;
    if (editable === 'always') {
      return;
    }
    if (this.state.datePickerOpen) {
      return;
    }
    if (editable) {
      this.setState({ editMode: false });
    }
  }

  decorateValue = value => {
    const { date, currency, link, badge, badge2, numeric, long } = this.props;
    if (date) {
      if (date === 'timeago') {
        value = moment(value).fromNow();
      } else {
        value = this.formatDate(value, date);
      }
    } else if (currency) {
      value = this.formatCurrency(value, currency);
    }
    if (link && value) {
      value = this.makeLink(value, link);
    }
    if (badge) {
      value = this.makeBadge(value, badge);
    }
    if (badge2) {
      value = this.makeBadge2(value);
    }
    if (numeric) {
      value = this.formatNumber(value);
    }
    if (long) {
      this.tooltipRef = React.createRef();
      this.cellRef = React.createRef();
      value = (
        <div ref={this.cellRef}>
          <CellTooltip innerRef={this.tooltipRef} cell={this.cellRef} value={value} />
        </div>
      )
    }
    return value
  }

  render() {
    const { editMode } = this.state;
    const { numeric, editable, classes, settings, dataCellProps, value } = this.props;
    const textAlign = numeric ? 'right' : 'left';
    if (editMode && editable) {
      return this.makeEditable();
    }
    return (
      <TableCell
        classes={{
          root: getDensityClass(classes, 'Cell', settings.density)
        }}
        style={{textAlign, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
        onClick={this.handleClick}
        onDoubleClick={this.openEditor}
        {...dataCellProps}
      >
        {this.decorateValue(value)}
      </TableCell>
    )
  }
}

DataCell.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  id: PropTypes.string.isRequired
};

export default withStyles(styles, { withTheme: true })(DataCell);
